import { parsePhoneNumber } from 'libphonenumber-js';
import { isRef } from 'vue';
import { mapState } from '@/store/helper/vuex';

const widgetHelper = require('@/scripts/widgetHelper');

const { enableGtm, dyEnabled } = window.vue.customPreferences;

const COUNTRIES_COORDS = {
    BE: {
        lat: 50.83333333,
        lng: 4
    },
    DE: {
        lat: 51,
        lng: 9
    },
    ES: {
        lat: 40,
        lng: -4
    },
    FR: {
        lat: 46,
        lng: 2
    },
    IT: {
        lat: 42.83333333,
        lng: 12.83333333
    },
    LU: {
        lat: 49.75,
        lng: 6.16666666
    },
    NL: {
        lat: 52.5,
        lng: 5.75
    }
};

const getCollisimoMapSettings = (country) => ({
    center: COUNTRIES_COORDS[country],
    zoom: 4,
    styles: [
        {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#e9e9e9'
                },
                {
                    lightness: 17
                }
            ]
        },
        {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#f5f5f5'
                },
                {
                    lightness: 20
                }
            ]
        },
        {
            featureType: 'road.highway',
            elementType: 'geometry.fill',
            stylers: [
                {
                    color: '#ffffff'
                },
                {
                    lightness: 17
                }
            ]
        },
        {
            featureType: 'road.highway',
            elementType: 'geometry.stroke',
            stylers: [
                {
                    color: '#ffffff'
                },
                {
                    lightness: 29
                },
                {
                    weight: 0.2
                }
            ]
        },
        {
            featureType: 'road.arterial',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#ffffff'
                },
                {
                    lightness: 18
                }
            ]
        },
        {
            featureType: 'road.local',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#ffffff'
                },
                {
                    lightness: 16
                }
            ]
        },
        {
            featureType: 'poi',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#f5f5f5'
                },
                {
                    lightness: 21
                }
            ]
        },
        {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#dedede'
                },
                {
                    lightness: 21
                }
            ]
        },
        {
            elementType: 'labels.text.stroke',
            stylers: [
                {
                    visibility: 'on'
                },
                {
                    color: '#ffffff'
                },
                {
                    lightness: 16
                }
            ]
        },
        {
            elementType: 'labels.text.fill',
            stylers: [
                {
                    saturation: 36
                },
                {
                    color: '#333333'
                },
                {
                    lightness: 40
                }
            ]
        },
        {
            elementType: 'labels.icon',
            stylers: [
                {
                    visibility: 'off'
                }
            ]
        },
        {
            featureType: 'transit',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#f2f2f2'
                },
                {
                    lightness: 19
                }
            ]
        },
        {
            featureType: 'administrative',
            elementType: 'geometry.fill',
            stylers: [
                {
                    color: '#fefefe'
                },
                {
                    lightness: 20
                }
            ]
        },
        {
            featureType: 'administrative',
            elementType: 'geometry.stroke',
            stylers: [
                {
                    color: '#fefefe'
                },
                {
                    lightness: 17
                },
                {
                    weight: 1.2
                }
            ]
        }
    ]
});

const getStaticIcon = (iconName, extension) => `${window.vue.variables.staticImagesFolder}/${iconName}.${extension}`;
const svgIcon = (iconName) => getStaticIcon(iconName, 'svg');

const pngIcon = (iconName) => getStaticIcon(iconName, 'png');

const jpgIcon = (iconName) => getStaticIcon(iconName, 'jpg');

const SvgHelper = {
    name: 'SvgHelper',
    props: {
        icon: {
            type: String,
            required: true
        },
        decorative: Boolean,
        svgtitle: String,
        addTabindex: Boolean,
        role: String
    },
    setup() {
        const { svgPath } = mapState();
        return {
            svgPath
        };
    },
    template: `
    <svg :class="['svg-icon', icon +'-svg', decorative ? 'decorative-icon' : '']"
        :aria-label="svgtitle"
        :focusable="decorative === true ? false : null"
        :aria-hidden="decorative === true ? true : null"
        :tabindex="addTabindex === true ? 0 : null"
        :role="role || 'img'">
        <use :href="svgPath + '#' + icon" />
    </svg>`

};

const stringAttributesToObjects = (attributesString) => {
    if (typeof attributesString === 'string') {
        const attributesObject = {};
        if (attributesString && attributesString.length > 0) {
            const attributesArray = attributesString.replaceAll('= "', '').replaceAll('"', '').split(' ');
            for (let i = 0; i <= attributesArray.length / 2; i += 2) {
                attributesObject[attributesArray[i]] = attributesArray[i + 1];
            }
        }
        return attributesObject;
    }
    return attributesString;
};

const scrollAnimate = (element) => {
    if (element) {
        element.focus({ preventScroll: true });
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
};

// https://gist.github.com/ahtcx/0cd94e62691f539160b32ecda18af3d6?permalink_comment_id=4058994
const clone = (obj, isStrictlySafe = true) => {
    /* Clones an object. First attempt is safe. If it errors (e.g. from a circular reference),
          'isStrictlySafe' determines if error is thrown or an unsafe clone is returned. */
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch (err) {
        if (isStrictlySafe) { throw new Error(err); }
        console.warn('Unsafe clone of object', obj);
        return { ...obj };
    }
};

const merge = (target, source, { isMutatingOk = false, isStrictlySafe = true } = {}) => {
    if (Array.isArray(target) === false) {
        /* Returns a deep merge of source into target.
              Does not mutate target unless isMutatingOk = true. */
        target = isMutatingOk ? target : clone(target, isStrictlySafe);
        const entries = Object.entries(source);
        entries.forEach(([key, val]) => {
            if (val !== null && typeof val === 'object') {
                if (target[key] === undefined || target[key] === null) {
                    target[key] = Object.getPrototypeOf(val).constructor();
                }
                /* even where isMutatingOk = false, recursive calls only work on clones, so they can always
                      safely mutate --- saves unnecessary cloning */

                // Another edge case when merging refs
                if (isRef(val) === false) {
                    target[key] = merge(target[key], val, { isMutatingOk: true, isStrictlySafe });
                } else {
                    // target[key] = computed({ get: () => { return val }});
                    target[key] = val;
                }
            } else {
                // Edge Case, if target is null or undefined instead of empty object
                if (target === null || target === undefined) {
                    target = {};
                }
                target[key] = val;
            }
        });
        return target;
    } target = source;
    return target;
};
// https://gist.github.com/ahtcx/0cd94e62691f539160b32ecda18af3d6?permalink_comment_id=4058994

const mergeDynamicForm = (target, source, { mergeModeSupport = false } = {}) => {
    if (Array.isArray(target) === false) {
        const targetKeys = Object.keys(target);

        let entries = Object.entries(source);
        entries.forEach(([key, val]) => {
            const objectHasTargetKey = targetKeys.includes(key);

            if (objectHasTargetKey) {
                if (mergeModeSupport && target[key] && typeof target[key] === 'object' && target[key].mergeMode) {
                    switch (target[key].mergeMode) {
                        case 'merge':
                            console.log('merging, target[key], key, val, target[key].value', target[key], key, val, target[key].value);
                            // eslint-disable-next-line no-unused-expressions
                            typeof target[key].value === 'string' && (target[key] = target[key].value.concat(' ', val));
                            break;
                        // this is the default case when mergeMode is not specified
                        case 'overwrite':
                            console.log('overwriting, target[key], key, val, target[key].value', target[key], key, val, target[key].value);
                            target[key] = target[key].value;
                            break;
                        case 'disabled':
                            target[key] = source[key];
                            break;
                        default:
                    }
                } else if (typeof target[key] === 'object' && typeof val === 'object' && isRef(val) === false) {
                    target[key] = mergeDynamicForm(target[key], val, { mergeModeSupport });
                }
            } else {
                target[key] = val;
            }
        });

        if (mergeModeSupport) {
            entries = Object.entries(target);
            entries.forEach(([key, val]) => {
                if (target[key] && typeof target[key] === 'object' && target[key].mergeMode) {
                    if (target[key].mergeMode !== 'disabled') {
                        target[key] = val.value;
                    } else if (source[key]) {
                        target[key] = source[key];
                    } else {
                        delete target[key];
                    }
                }
            });
        }

        return target;
    }
    target = source;
    return target;
};

const objectToFormData = (object) => {
    const formData = new FormData();
    Object.keys(object).forEach((key) => formData.append(key, object[key]));
    return formData;
};

const normalizeCardType = (cardType) => {
    let normalized = cardType;
    switch (cardType.toLowerCase()) {
        case '001':
        case 'visa':
            normalized = 'Visa';
            break;
        case '002':
        case 'mc':
        case 'mastercard':
            normalized = 'MasterCard';
            break;
        case '003':
        case 'amex':
            normalized = 'Amex';
            break;
        case '004':
        case 'discover':
            normalized = 'Discover';
            break;
        case '042':
        case 'maestro':
            normalized = 'Maestro';
            break;
        case '005':
        case 'diners':
        case 'dinersclub':
            normalized = 'DinersClub';
            break;
        case '007':
        case 'jcb':
            normalized = 'JCB';
            break;
        case 'cartebancaire':
            normalized = 'Cartebancaire';
            break;
        default:
            break;
    }

    return normalized;
};

// eslint-disable-next-line no-unused-vars
const filterObject = (obj) => Object.fromEntries(Object.entries(obj).filter(([key, value]) => /* value !== null && value !== '' && */ value !== undefined));

const formatPhoneNumber = (phoneNumber, countryCode) => {
    if (!phoneNumber || !phoneNumber.trim()) {
        return {};
    }
    var phoneNumberParsed = parsePhoneNumber(phoneNumber, countryCode);

    return {
        national: phoneNumberParsed.formatNational() || phoneNumber,
        international: phoneNumberParsed.formatInternational() || phoneNumber
    };
};

const areSuggestedAddresses = (data) => {
    if (!data.addressesVerification || data.addressesVerification.addresses.length === 0) {
        return false;
    }

    const suggestedAddress = data.addressesVerification.addresses[0];

    try {
        if (data.address.address1.toLowerCase() !== suggestedAddress.street.toLowerCase()) {
            return true;
        }

        if (data.address.city.toLowerCase() !== suggestedAddress.city.toLowerCase()) {
            return true;
        }

        if (data.address.countryCode.toLowerCase() !== suggestedAddress.country.toLowerCase()) {
            return true;
        }

        if (data.address.stateCode.toLowerCase() !== suggestedAddress.state.toLowerCase()) {
            return true;
        }

        if (data.address.postalCode.toLowerCase() !== suggestedAddress.zip.toLowerCase()) {
            return true;
        }
    } catch (e) { }

    return false;
};

const handleTracking = (data) => {
    if (enableGtm) {
        if (data.order && data.order.shipping[0].selectedShippingMethod && data.order.shipping[0].selectedShippingMethod.displayName) {
            document.dispatchEvent(new CustomEvent('gtm_payment', {
                detail: ['2', data.order.shipping[0].selectedShippingMethod.displayName, data.hashedEmail]
            }));
        }
        if (data.form.shippingAddress.shippingEmailAddress.subscribe !== null && data.form.shippingAddress.shippingEmailAddress.subscribe.checked) {
            document.dispatchEvent(new CustomEvent('gtm:newsletter', {
                detail: ['Checkout', data.hashedEmail]
            }));
        }
    }

    if (dyEnabled) {
        dynamicYield.identify(data.hashedEmail);
        if (data.guestEmail) {
            if (data.userSubscribed) {
                dynamicYield.signUpNewsLetter('', data.guestEmail); // eslint-disable-line
            }
            dynamicYield.messageOptIn(data.guestEmail);
        }
    }
};

const smartRedirect = (data) => {
    if (data.redirectUrl) {
        const redirectUrlObj = new URL(data.redirectUrl.includes(window.location.host) ? data.redirectUrl : `https://${window.location.host}${data.redirectUrl}`);
        const queryParams = Object.fromEntries(redirectUrlObj.searchParams.entries());

        if (data.redirectUrl.toLowerCase().includes(defaultPath.toLowerCase())) {
            document.dispatchEvent(new CustomEvent('route-to', { detail: { path: '/', query: queryParams } }));
        } else if (data.redirectUrl.toLowerCase().includes(checkoutPath.toLowerCase())) {
            document.dispatchEvent(new CustomEvent('route-to', { detail: { path: '/checkout', query: queryParams } }));
        } else {
            window.location.href = data.redirectUrl;
        }
    }
};

const handleResponseError = (commit, dispatch, response) => {
    if (!response) {
        window.location.href = window.vue.actionURLs['Home-Show'];
    }

    if (isDataDomeRedirect(response)) {
        window.location.href = response.request.responseURL;
    }

    widgetHelper.spinner.stop();

    const data = response.data;

    commit('handleCheckoutError', data);

    if (data.cartError || data.redirectUrl) {
        smartRedirect(data);
    }

    if (data.fieldErrors) {
        let fieldErrors = data.fieldErrors;
        const isFieldErrorsArray = Array.isArray(data.fieldErrors);
        if (!isFieldErrorsArray) {
            var fieldErrorsEntries = Object.entries(data.fieldErrors);
            if (fieldErrorsEntries && fieldErrorsEntries.length > 0) {
                fieldErrors = fieldErrorsEntries.map(([key, value]) => { const obj = {}; obj[key] = value; return obj; });
            }
        }
        if (fieldErrors.length > 0) {
            dispatch('handleFieldErrors', fieldErrors);
        }
    }

    if (data.areUshippableItems) {
        dispatch('getCart', { keepSpinner: false }, { root: true });
    }

    return {
        success: false,
        data
    };
};

function getNestedDynamicStructureFieldParentKey(nestedDynamicStructure, fieldName) {
    const keys = Object.keys(nestedDynamicStructure);
    keys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(nestedDynamicStructure, key) && typeof nestedDynamicStructure[key] === 'object') {
            const rows = Object.keys(nestedDynamicStructure[key]);
            rows.forEach((row) => {
                const fields = nestedDynamicStructure[key][row].fields;
                fields.forEach((field) => {
                    if (field.name === fieldName) {
                        return key;
                    }
                });
            });
        }
    });
    return null;
}

function getFieldsFromDynamicStructure(dynamicFormStructure) {
    var values = Object.values(dynamicFormStructure);
    return values
        .flatMap((group) => (Object.prototype.hasOwnProperty.call(group, 'fields') ? group.fields : Object.values(group).flatMap((row) => row.fields || [])))
        .reduce((fieldOptionsObject, field) => {
            fieldOptionsObject[field.name] = { ...field };
            return fieldOptionsObject;
        }, {});
}

function getFEAdyenPaymentMethods(paymentMethod) {
    const mapping = window.checkout.customPreferences.adyenPaymentMethodsMap;
    var keys = Object.keys(mapping);
    return keys.filter((key) => mapping[key] === paymentMethod);
}

function getBEAdyenPaymentMethod(paymentMethod) {
    const mapping = window.checkout.customPreferences.adyenPaymentMethodsMap;
    return mapping[paymentMethod];
}

function getLineItemsGtmData(items, totals) {
    const result = {
        cartTotal: totals.grandTotalValue,
        lineItems: [],
        coupon: ''
    };

    let coupons = {};

    result.lineItems = items.map((item) => {
        const gtmData = Object.assign({}, item.gtmData);
        gtmData.quantity = item.quantity;
        coupons[gtmData.coupon] = gtmData.coupon;
        return gtmData;
    });

    result.coupon = Object.keys(coupons).filter((coupon) => coupon).join(',');

    return result;
}

function isDataDomeRedirect(response) {
    return response.request.responseURL.includes('DDUser-Challenge');
}

function submitOrderConfirm(url, orderId, orderToken, points) {
    const form = document.createElement('form');

    const orderIdEl = document.createElement('input');
    orderIdEl.value = orderId;
    orderIdEl.name = 'orderID';

    const orderTokenEl = document.createElement('input');
    orderTokenEl.value = orderToken;
    orderTokenEl.name = 'orderToken';

    const pointsEl = document.createElement('input');
    pointsEl.value = points;
    pointsEl.name = 'points';

    form.method = 'POST';
    form.action = url;

    form.appendChild(orderIdEl);
    form.appendChild(orderTokenEl);
    form.appendChild(pointsEl);

    document.body.appendChild(form);

    form.submit();
}

export {
    getFieldsFromDynamicStructure,
    mergeDynamicForm,
    getNestedDynamicStructureFieldParentKey,
    svgIcon,
    pngIcon,
    jpgIcon,
    SvgHelper,
    stringAttributesToObjects,
    scrollAnimate,
    merge,
    objectToFormData,
    normalizeCardType,
    filterObject,
    formatPhoneNumber,
    getCollisimoMapSettings,
    areSuggestedAddresses,
    handleTracking,
    handleResponseError,
    smartRedirect,
    getFEAdyenPaymentMethods,
    getBEAdyenPaymentMethod,
    getLineItemsGtmData,
    isDataDomeRedirect,
    submitOrderConfirm
};
